import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ObjectionBreakdownRow, ReportFilters } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

/** "Why we're losing, ranked — the most actionable report" (design doc §8). */
@Component({
  selector: 'app-objection-breakdown',
  standalone: true,
  imports: [
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './objection-breakdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./objection-breakdown.component.css', '../../../../css/report-print.css'],
})
export class ObjectionBreakdownComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  rows = signal<ObjectionBreakdownRow[]>([]);
  loading = signal(false);

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.loading.set(true);
    try {
      this.rows.set(await this.reportsService.getObjectionBreakdown(filters, this.includeAllUsers));
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('objection-breakdown'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('objection-breakdown'), 'Objection Breakdown');
  }

  private exportRows(): Record<string, string | number>[] {
    return this.rows().map((r) => ({
      Objection: r.objectionLabel,
      Count: r.count,
      '% of total': `${(r.percentOfTotal * 100).toFixed(1)}%`,
    }));
  }
}
